import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LangToggle } from "@/components/common/LangToggle";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function Shell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#0F172A] p-12 text-white lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="RinoTECH" className="h-14 w-14 rounded-lg bg-white p-1" data-testid="auth-logo" />
          <span className="font-display text-xl font-extrabold">RinoTECH</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Taxes y presencia digital, en un solo lugar.</h2>
          <p className="mt-4 text-slate-400">Prospectos, clientes, propuestas, proyectos y tareas con acceso seguro para su equipo y sus clientes.</p>
        </div>
        <p className="text-xs text-slate-500">America/Chicago · USD</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-6 flex items-center justify-between"><Link to="/planes" className="text-sm font-semibold text-blue-700 hover:underline" data-testid="login-plans-link">Ver planes</Link><LangToggle /></div>
          <div className="mb-8 flex items-center gap-2.5 lg:hidden" data-testid="auth-logo-mobile">
            <img src="/logo.png" alt="RinoTECH" className="h-14 w-14" />
            <span className="font-display text-xl font-extrabold text-slate-900">RinoTECH</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <p className="mb-8 mt-2 text-sm text-slate-500">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

const Err = ({ text }) => text ? <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="auth-error">{text}</p> : null;

export const homeFor = (u, next) => {
  const n = next || "";
  const safe = n.startsWith("/") && !n.startsWith("//") && !n.includes("\\") && (u.role === "client") === n.startsWith("/portal");
  return safe ? n : u.role === "client" ? "/portal" : "/";
};

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u, params.get("next")), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Iniciar sesión" subtitle="Ingrese con su correo y contraseña.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><label className="field-label" htmlFor="email">Correo</label>
          <input id="email" type="email" required autoComplete="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></div>
        <div><label className="field-label" htmlFor="password">Contraseña</label>
          <input id="password" type="password" required autoComplete="current-password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" /></div>
        <Err text={error} />
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Entrar</Button>
        <Link to="/recuperar-contrasena" className="block text-center text-sm text-blue-700 hover:underline" data-testid="forgot-password-link">¿Olvidó su contraseña?</Link>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Recuperar contraseña" subtitle="Le enviaremos un enlace válido por 1 hora.">
      {done ? (
        <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-success">Si el correo está registrado, recibirá un enlace para restablecer su contraseña.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><label className="field-label" htmlFor="femail">Correo</label>
            <input id="femail" type="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <Err text={error} />
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enviar enlace</Button>
        </form>
      )}
      <Link to="/login" className="mt-6 block text-center text-sm text-blue-700 hover:underline" data-testid="back-to-login-link">Volver a iniciar sesión</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError("La contraseña debe tener al menos 8 caracteres");
    if (pw !== pw2) return setError("Las contraseñas no coinciden");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Nueva contraseña" subtitle="Elija una contraseña de al menos 8 caracteres.">
      {done ? (
        <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="reset-success">Contraseña actualizada. Ya puede iniciar sesión.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><label className="field-label" htmlFor="pw">Nueva contraseña</label>
            <input id="pw" type="password" className="field-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
          <div><label className="field-label" htmlFor="pw2">Confirmar contraseña</label>
            <input id="pw2" type="password" className="field-input" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" /></div>
          <Err text={error} />
          <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar contraseña</Button>
        </form>
      )}
      <Link to="/login" className="mt-6 block text-center text-sm text-blue-700 hover:underline">Ir a iniciar sesión</Link>
    </Shell>
  );
}
